import React, { useState } from "react";
import { notification } from "antd";
import ApiService from "../../../services/ApiService";
import Icon from "../components/Icon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const styles = `
.t26-newsletter-wrap {
  /* Gutter latéral aligné sur celui du hero/sections (clamp(28px, 4.8vw, 64px))
     pour que la card newsletter ne déborde pas du rythme visuel de la page. */
  padding: clamp(28px, 4vw, 56px) clamp(28px, 4.8vw, 64px);
  max-width: calc(var(--container) + 80px);
  margin-inline: auto;
  box-sizing: border-box;
}
.t26-newsletter {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: clamp(20px, 2.4vw, 28px);
  padding: clamp(28px, 4vw, 48px);
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: clamp(20px, 3vw, 36px);
  align-items: center;
  box-shadow: var(--shadow-sm);
}
@media (max-width: 720px) {
  .t26-newsletter { grid-template-columns: 1fr; text-align: center; }
}

/* Mobile : on sécurise pour éviter tout overflow du form (input + bouton). */
.t26-newsletter,
.t26-newsletter__form,
.t26-newsletter__input {
  min-width: 0;
}
@media (max-width: 560px) {
  /* Sur petit écran le form passe en colonne (input pleine largeur + bouton dessous) */
  .t26-newsletter__form {
    flex-direction: column;
    align-items: stretch;
    padding: 8px;
    border-radius: var(--r-lg);
    gap: 6px;
  }
  .t26-newsletter__input {
    padding: 12px 12px;
    text-align: start;
  }
  .t26-newsletter__submit {
    justify-content: center;
    padding: 12px 18px;
    width: 100%;
  }
}
.t26-newsletter__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase;
  color: var(--secondary);
  margin-bottom: 10px;
}
.t26-newsletter__title {
  font-family: var(--font-display);
  margin: 0;
  font-size: clamp(22px, 3vw, 32px);
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
  text-wrap: balance;
}
.t26-newsletter__desc {
  margin: 10px 0 0;
  font-size: 15px;
  color: var(--ink-3);
  text-wrap: pretty;
}
.t26-newsletter__form {
  display: flex; gap: 8px;
  width: 100%;
  background: var(--bg);
  border: 1px solid var(--line);
  border-radius: var(--r-pill);
  padding: 6px 6px 6px 18px;
  align-items: center;
}
.t26-newsletter__form:focus-within {
  border-color: var(--primary);
}
.t26-newsletter__input {
  flex: 1;
  background: transparent;
  border: 0;
  outline: 0;
  padding: 12px 0;
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--ink);
}
.t26-newsletter__input::placeholder { color: var(--ink-3); }
.t26-newsletter__submit {
  padding: 10px 20px;
  background: var(--primary);
  color: var(--primary-contrast);
  border: 0;
  border-radius: var(--r-pill);
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  transition: transform .15s ease;
}
.t26-newsletter__submit:hover { transform: translateY(-1px); }
.t26-newsletter__submit:disabled { opacity: .6; cursor: progress; }
`;

export default function Newsletter({ context, eyebrow, title, description, ctaLabel }) {
    const { org } = context;
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const finalEyebrow = eyebrow || t`Newsletter`;
    const finalTitle = title || t`Suivez-nous`;
    const finalDesc = description || t`Recevez nos actualités, événements et inspirations directement par email.`;
    const finalCta = ctaLabel || t`S'inscrire`;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await ApiService.get("/mailing/subscribe/" + org.id + "/" + encodeURIComponent(email));
            notification.success({
                message: t`Inscription réussie !`,
                description: t`Vous recevrez bientôt des nouvelles de notre part.`,
            });
            setEmail("");
        } catch (err) {
            notification.error({
                message: t`Inscription impossible`,
                description: t`Veuillez réessayer dans un instant.`,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="t26-newsletter-wrap">
                <div className="t26-newsletter">
                    <div>
                        <span className="t26-newsletter__eyebrow">
                            <Icon name="mail" size={12} />
                            {finalEyebrow}
                        </span>
                        <h2 className="t26-newsletter__title">{finalTitle}</h2>
                        <p className="t26-newsletter__desc">{finalDesc}</p>
                    </div>

                    <form className="t26-newsletter__form" onSubmit={onSubmit}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t`Votre adresse email`}
                            className="t26-newsletter__input"
                            required
                            disabled={loading}
                            aria-label={t`Adresse email`}
                        />
                        <button type="submit" className="t26-newsletter__submit" disabled={loading}>
                            {loading ? <Trans>Inscription…</Trans> : finalCta}
                            <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
}
